import { Component, ChangeDetectorRef, inject, OnDestroy } from '@angular/core';
import { DealerTrainingBillingReportFormArray, DealerTrainingBillingReportProps, DealerTrainingBillingReportTableSchema, DealerTrainingBillingExportSearchConfig, DealerTrainingBillingSearchConfig } from '../../model/dealer-training-billing-report.model';
import { PageContainerComponent } from '../../layout/page-container/page-container.component';
import { API_STATUS, ApiEnvNames, ERROR_RESPONSE, SearchConfig, ExportConfig } from '../../model/api.model';
import { CommonUtils } from '../../utils/common.utils';
import { DynamicFormArray, Props } from '../../model/form.model';
import { DataTableSchema } from '../../model/dynamic-data-table.model';
import { BaseComponent } from '../base/base.component';
import { CommonService } from '../../services/common.service';
import { PaginatorService } from '../../services/paginator-service.service';
import { GlobalApiService } from '../../services/global-api.service';
import { Subject, takeUntil } from 'rxjs';
import { EXCEL_EXTENSION, ExcelExportService } from '../../services/excel-export.service';
import { FieldNames } from '../../model/form-fields.model';

@Component({
  selector: 'app-dealer-training-billing-report',
  standalone: true,
  imports: [PageContainerComponent],
  templateUrl: './dealer-training-billing-report.component.html'
})
export class DealerTrainingBillingReportComponent extends BaseComponent implements OnDestroy {
  protected data = 'DealerTrainingBillingReportComponent';
  public dataTableSchema: DataTableSchema = structuredClone(DealerTrainingBillingReportTableSchema);
  public filterSchema: DynamicFormArray = structuredClone(DealerTrainingBillingReportFormArray);
  protected props: Props = DealerTrainingBillingReportProps;
  title = "";
  service = inject(CommonService);
  commonUtils = inject(CommonUtils);
  paginatorService = inject(PaginatorService);
  globalApi = inject(GlobalApiService);
  changeRef = inject(ChangeDetectorRef);
  excelService = inject(ExcelExportService);
  
  private isFirstLoad = true;
  isDataInitialized = false;
  currentPage: any;
  override sortBy: any = {};
  public tableFilter = '';
  readonly destroy$ = new Subject<void>();

  override ngOnInit() {
    super.ngOnInit();
    super.commonFunction();
    this.dataTableSchema.filterCallback = this.paginationFilterCallback;
    this.dataTableSchema.exportCallback = this.exportCallback;
    this.dataTableSchema.sortCallback = this.sortCallback; 
    this.paginatorService.pageEvent$.pipe(takeUntil(this.destroy$)).subscribe(event => {
      if (this.isFirstLoad) {
        this.isFirstLoad = false;
        if (this.dataTableSchema['pageSizeOptions']['pageSize']) {
          this.dataTableSchema['pageSizeOptions']['pageLength'] =
            this.dataTableSchema['pageSizeOptions']['pageSize'][0];
        }
      } else {
        this.currentPage = event;
        this.dataTableSchema.dataSource = [];
        this.setTableLoadingState(true);
        this.onSearchClicked('');
      }
    });
  }

  override onSearchClicked = (searchParam: any) => {
    if (searchParam) {
      this.resetSorting();
      this.resetDataTable();
    }
    const searchConfig = this.getSearchConfig();
    this.fetchAllData(searchConfig);
  };

paginationFilterCallback = (filterValue: string) => {
    if (this.isDataInitialized) {
      if (filterValue || (!filterValue && this.tableFilter)) {
        const searchConfig = this.getSearchConfig();
        this.tableFilter = filterValue;
        this.resetDataTable();
        this.fetchAllData(searchConfig);
      }
    }
  };

exportCallback = () => {
    const searchConfig = this.getSearchConfig();
    searchConfig['body']['download'] = true;
    this.fetchAllData(searchConfig, true);
  };

sortCallback = (fieldInfo: Array<Record<string, string>>) => {
    if (fieldInfo) {
      const sortBy: any = {};
      fieldInfo.forEach(field => {
        sortBy[field['id']] = field['direction'];
      });
      this.sortBy = sortBy;
      if (this.dataTableSchema.dataSource.length > 0) {
        const searchConfig = this.getSearchConfig();
        this.resetDataTable();
        this.fetchAllData(searchConfig);
      }
    }
  };

  override onResetClicked = () => {
    this.dataTableSchema.inputFilter = '';
    this.filterSchema.FieldsArray = this.filterSchema.FieldsArray.map((field) => ({ ...field, selectedValue: field['defaultValue'] }));
    this.resetDataTable();
    this.resetSorting();
    this.isDataInitialized = false;
    this.disableEnableButtons(true);
  };

  resetDataTable() {
    this.dataTableSchema.dataSource = [];
    if (this.dataTableSchema?.pageSizeOptions) {
      if (this.dataTableSchema?.pageSizeOptions.pageSize) {
        this.currentPage = {
          pageIndex: 0,
          pageSize: Number(this.currentPage?.pageSize) || this.dataTableSchema?.pageSizeOptions.pageSize[0],
        };
      }
    }
    this.dataTableSchema['pageSizeOptions']['pageLength'] = 0;
  }

getSearchConfig = (): any => {
    const searchConfig = structuredClone(DealerTrainingBillingSearchConfig)
    this.filterSchema['FieldsArray'].forEach(field => {
      if (field['selectedValue'] && field['selectedValue'] !== null && field['selectedValue'] !== '') {
        
        if(field['fieldName'] === FieldNames['CourseNameCode']){
          searchConfig['body'][FieldNames['CourseShortName']] = field['selectedValue']
        }else{
          searchConfig['body'][field['fieldName']] = field['selectedValue']
        }
      }
    });
    if (this.dataTableSchema.inputFilter.length > 0) {
      searchConfig['body']['searchText'] = this.dataTableSchema.inputFilter
    }
    if (this.sortBy && Object.keys(this.sortBy).length > 0) {
      if (!searchConfig['body']) {
        searchConfig['body'] = {}
      }
      searchConfig['body']['sortBy'] = this.sortBy;
    }
      return searchConfig
  };

  fetchAllData = (_searchConfig: SearchConfig, callFromExport = false) => {
    this.setTableLoadingState(true);
    const searchConfig = structuredClone(_searchConfig);
    
    if (!callFromExport) {
      searchConfig['body'] = searchConfig['body'] || {};
      searchConfig['body'].pageNo = Number(this.currentPage?.pageIndex ?? 0) + 1;
      searchConfig['body'].pageSize = Number(this.currentPage?.pageSize);
    }

    const exportQueryConfig = this.getExportConfig();
    const exportConfig: ExportConfig = {
      tableSchema: this.dataTableSchema,
      environment: ApiEnvNames['StaffMaster'],
      fileExtension: EXCEL_EXTENSION,
      searchParameters: DealerTrainingBillingExportSearchConfig,
      exportFileName: 'Course_Billing',
    };

    if (callFromExport) {
      searchConfig['body']['exportConfig'] = exportQueryConfig;
    }

    this.service.getDataFromDataSource(searchConfig, ApiEnvNames['DealerTrainingBilling']).then(async response => {
      if (response['status'] === API_STATUS['Success']) {
        if (Array.isArray(response.result)) {
          if (callFromExport) {
            await this.service.downloadExcelFromS3(response.result[0], exportConfig);
          } else {
            this.dataTableSchema['pageSizeOptions']['pageLength'] = response?.result?.[0]?.totalRecordsCount ?? 0;            this.dataTableSchema.dataSource = response.result;
            if (!this.isDataInitialized && response.result.length > 0) {
              this.isDataInitialized = true;
            }
            this.setTableLoadingState(false);
          }
        }
      } else if (callFromExport) {
        await this.service.recursiveCallS3(exportQueryConfig, exportConfig);
      } else {
        this.dataTableSchema.dataSource = [];
        this.commonUtils.showMatSnackBar(ERROR_RESPONSE['errorFetch']);
      }
      this.changeRef.detectChanges();
      this.setTableLoadingState(false);
    }).catch(err => {
      this.dataTableSchema.dataSource = [];
      console.info(ERROR_RESPONSE['layoutErrorPrefix'], err);
      this.setTableLoadingState(false);
    });
  };

  formatDate(date: string | Date): string {
    const d = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  }

  getExportConfig() {
    return {
      fileName: `Course_Billing_Export_${this.commonUtils.getFormattedDate()}.csv`,
      bucketName: this.commonUtils.getBucketName()
    };
  }
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
